// Angular
import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';

// Firebase
import { Auth, AuthProvider, authState, EmailAuthProvider, FacebookAuthProvider, getAuth, getRedirectResult, GoogleAuthProvider, idToken, OAuthProvider, sendPasswordResetEmail, signInWithCustomToken, signOut, updatePassword } from '@angular/fire/auth';
import { signInWithPopup, linkWithPopup, signInWithEmailAndPassword, signInAnonymously, signInWithCredential, linkWithCredential, createUserWithEmailAndPassword } from '@angular/fire/auth';
import type { UserCredential as FirebaseUserCredential, User as FirebaseUser } from '@angular/fire/auth';

// RxJS
import { Observable, Subject, ReplaySubject, merge, firstValueFrom } from 'rxjs';
import { first, map, filter, mergeMap, shareReplay } from 'rxjs/operators';

// StoryHunt
import { User, LoginUser } from '../models';
import { DreamHackLoginService } from './dreamhack-login.service';
import { AppleLoginService } from './apple-login.service';
import { LoginMigrationData, LoginUserInfo } from './motes-login.models';
import { LoginReq, LoginRsp, UserRights, LoginOptions, LoginFunction, LoginProvider, LoginCredential } from './motes-login.models';
import { FirebaseError } from '@angular/fire/app';
import { getAdditionalUserInfo, UserCredential } from 'firebase/auth';
import { OidcLoginService } from './oidc-login.service';

export { LoginOptions, UserRights, FirebaseUserCredential };

export enum LoginState {
    SIGN_IN_SELECTION = "SIGN_IN_SELECTION",
    SIGN_IN_WITH_EMAIL = "SIGN_IN_WITH_EMAIL",
    CREATE_NEW_ACCOUNT = "CREATE_NEW_ACCOUNT",
    ANONYMOUS_ADMIN_REQ = "ANONYMOUS_ADMIN_REQ",
    VERIFY_ADMIN = "VERIFY_ADMIN",
    VERIFICATION_DONE = "VERIFICATION_DONE",
    SIGN_IN_DONE = "SIGN_IN_DONE",
    RECOVER_PASSWORD = "RECOVER_PASSWORD",
    NEW_OR_EXISTING_EMAIL_USER = "NEW_OR_EXISTING_EMAIL_USER",
    GET_EMAIL_PERMISSION = "GET_EMAIL_PERMISSION"
}

export interface UserFactory {
    /**
    * Create a new anonymous user (without profile pic and email) in user database and register a login
    * @param credentials Firebase credentials with provider data
    */
    createAnonymousUser(credentials: LoginCredential, userRights: UserRights): Promise<any>;

    /**
    * Create a new user in user database and register a login
    * @param credentials Firebase credentials with provider data
    * @param username Name of the user
    */
    createUser(credentials: LoginCredential, userRights: UserRights, username: string, removeMigrationData: boolean): Promise<any>;

    /**
    * Create a admin request entry in user database
    */
    createAdminRequest(user: User): Promise<any>;

    /**
    * Test if the user already exists in database
    * @param userKey
    */
    userExists(userKey: string): Promise<boolean>;

    /**
    * Watch database user
    */
    watchCurrentUser(): Observable<User | null>;

    /**
    * Get User
    */
    getUser(uid: string): Promise<User>;
}

@Injectable({
    providedIn: 'root'
})
export class MotesLoginService {

    public options: LoginOptions = {
        signInWithPopup: true,
        facebook: true,
        google: true,
        apple: true,
        email: true,
        azure: true,
        enableAdminRequest: false,
        enableEmailVerification: false
    };

    // Factory used to create new users when they signup
    public userFactory: UserFactory = null;

    // User states
    public adminUser: User = null;
    // Store a public user rights instance for convinience (can be used directly in view)
    public userRights: UserRights = new UserRights;

    // Overlay control
    public showLoginOverlay: boolean = false;
    public loginOverlayText: string = "You need to sign in to perform this action.";
    public loginOverlayState: LoginState = LoginState.SIGN_IN_SELECTION;
    private externalLoginFunction: LoginFunction = null;

    private loginResponses: ReplaySubject<LoginRsp> = new ReplaySubject(1);
    // Shared user right observable for all subscribers to enjoy
    private firebaseUserSubject: Subject<FirebaseUser> = new Subject<FirebaseUser>();
    private userRightObservable: Observable<UserRights> = null;
    // Used to unlock owner privileges in Creator
    public static superAdminModeEnabled: boolean = false;

    constructor( private auth: Auth, private dreamHackLoginService: DreamHackLoginService, private appleLoginService: AppleLoginService, private oidcLoginService: OidcLoginService) {
        if(typeof window == 'undefined') return;
        // Expose login service to Cypress e2e test
        if((window as any)?.Cypress) {
            (window as any).MotesLoginService = this;
        }
    }

    /**
    * Initialize login service
    */
    public async init(userFactory: UserFactory = null, options: LoginOptions = this.options): Promise<void> {
        // Set default external function (OBS: this function might be overwritten for each login request)
        this.externalLoginFunction = options.externalLoginFunction || null;
        this.userFactory = userFactory;
        // Apply optional options if any
        Object.assign(this.options, options);
        return;
    }

    /**
    * Await next succesfull login response (automatic or user initiated login)
    */
    public nextLoginResponse(): Promise<LoginRsp> {
        return this.loginResponses.pipe(filter(rsp => rsp && rsp.userRights != null && rsp.err == ""), first()).toPromise();
    }

    /**
    * Continious watch user rights and whenever there is no signed in user,
    * create a new anonymous user login
    */
    public enableAnonymousLogin(): Observable<LoginRsp | null> {
        // If we already have user rights, dont do anything
        return this.watchUserRights().pipe(mergeMap((userRights) => {
            if(!userRights) {
                // Return flat mapped login response of new anonymous login
                return this.loginAnonymous();
            }
            else {
                // Generate
                let loginRsp = { msg: "Auto login", err: "", isNewUser: false, userRights: userRights };
                this.loginResponses.next(loginRsp);
                // Return flat mapped auto login response
                return Promise.resolve(loginRsp);
            }
        }));
    }

    /**
    * Get the current firebase user
    */
    public getCurrentUser(): Promise<FirebaseUser> {
        return firstValueFrom(this.watchCurrentUser());
    }

    /**
    * Get the current firebase user
    */
    public watchCurrentUser(): Observable<FirebaseUser> {
        return authState(this.auth);
    }

    /**
    * Get the current user rights
    * @return Resolved promise with null if no user is signed in
    */
    public getUserCurrentRights(): Promise<UserRights> {
        return this.watchUserRights().pipe(first()).toPromise();
    }

    /**
    * Get the user rights present in firebase user token
    * Immediately return null if no user is logged in
    * @return Returns promise with null or currently signed in users rights
    */
    public watchUserRights(): Observable<UserRights | null> {
        if(!this.userRightObservable) {

            let watchAuth = authState(this.auth);
            let watchUpdates = this.firebaseUserSubject.asObservable();

            this.userRightObservable = merge(watchUpdates, watchAuth).pipe(
                map(user => {
                    // Get user rights from token and notify listners
                    return user ? this.getUserRights(user) : Promise.resolve(null);
                }),
                mergeMap(p => p),
                shareReplay(1),
            );
        }
        return this.userRightObservable;
    }

    /**
    * Get user rights object from firebase token
    */
    private getUserRights(user: FirebaseUser | LoginUserInfo): Promise<UserRights> {
        return new Promise(async (resolve, reject) => {
            if(!user) {
                console.error("No user given, cant get user rights");
                this.userRights = new UserRights();
                reject({});
            }
            // Interpret user token
            let rights = await user.getIdTokenResult();

            this.userRights = {
                uid: user.uid,
                isAnonymous: rights.signInProvider == "anonymous",
                isAdmin: Boolean(rights.claims.isAdmin),
                isSuperAdmin: Boolean(rights.claims.isSuperAdmin),
                isVerified: Boolean(rights.claims.isVerified),
                authToken: rights.token
            };
            // Resolve with new user rights object
            resolve(this.userRights);
        });
    }

    /**
    * Request login with admin user rights (non anonymous, verified with admin rights).
    * If a user with sufficient user rights is not already signed in,
    * show the login overlay to the user and wait.
    * @return Promise witch is resolved when a user with sufficient user rights as requested are logged in
    */
    public requestSuperAdminLogin(msg: string = 'Please sign in to see this page.'): Promise<LoginRsp> {
        // Create a login request with super admin rights
        let loginReq: LoginReq = {
            msg: msg,
            requireRights: { isAnonymous: false, isAdmin: true, isSuperAdmin: true, isVerified: true }
        };

        return this.requestLogin(loginReq);
    }

    /**
    * Request login with admin user rights (non anonymous, verified with admin rights).
    * If a user with sufficient user rights is not already signed in,
    * show the login overlay to the user and wait.
    * @return Promise witch is resolved when a user with sufficient user rights as requested are logged in
    */
    public requestAdminLogin(msg: string = 'Please sign in to see this page.'): Promise<LoginRsp> {
        // Create a login request with super admin rights
        let loginReq: LoginReq = {
            msg: msg,
            requireRights: { isAnonymous: false, isAdmin: true, isSuperAdmin: false, isVerified: true }
        };

        return this.requestLogin(loginReq);
    }

    /**
    * Request login with normal user rights (non anonymous aka verified user login).
    * If a user with sufficient user rights is not already signed in,
    * show the login overlay to the user and wait.
    * @return Promise witch is resolved when a user with sufficient user rights as requested are logged in
    */
    public requestUserLogin(msg: string = 'Please sign in to see this page.', externalLogin: LoginFunction = null): Promise<LoginRsp> {
        // Create a login request with super admin rights
        let loginReq: LoginReq = {
            msg: msg,
            externalLoginFunction: externalLogin,
            requireRights: { isAnonymous: false, isAdmin: false, isSuperAdmin: false, isVerified: false }
        };

        return this.requestLogin(loginReq);
    }

    /**
    * Use requestAdminLogin and requestUserLogin helper functions to request login.
    * Request login with the given user rights. If a user with sufficient user rights
    * is not already signed in, show the login overlay to the user.
    * @return Promise witch is resolved when a user with sufficient user rights as requested are logged in
    */
    private async requestLogin(req: LoginReq, maxAttempts: number = 10): Promise<LoginRsp> {
        // Save externalLogin setting for handling user login button
        this.externalLoginFunction = req.externalLoginFunction ? req.externalLoginFunction : this.externalLoginFunction || null;

        // Get current user rights
        let userRights = await this.watchUserRights().pipe(first()).toPromise();
        let loginRsp: LoginRsp = { msg: "User accepted", err: "", userRights: userRights, isNewUser: false };

        for(let loginAttempt = 1; loginAttempt < maxAttempts; loginAttempt++) {
            // Test if user rights leve up to requested rights
            let areUserRightsOk = this.checkUserRights(req.requireRights, userRights);
            if(areUserRightsOk) {
                // Notify listeners
                this.loginResponses.next(loginRsp);
                //console.log("User rights are ok, resolve request promise ", loginRsp);
                return loginRsp;
            }
            else {
                // User rights are not ok, clear current login rsp (if any)
                this.loginResponses.next(null);
            }

            // Show login msg
            this.loginOverlayText = req.msg;
            this.showLoginOverlay = true;
            // Show admin request status if userRights are there, but user dosent have enough permissions and options is enabled
            if(userRights && this.options.enableAdminRequest) {
                this.loginOverlayState = userRights.isAnonymous ? LoginState.ANONYMOUS_ADMIN_REQ : LoginState.VERIFY_ADMIN;
                // Await super admin to give admin user rights
                console.warn("User rights are not sufficient (no admin rights)", userRights);
                let loginRsp: LoginRsp = { msg: "User rejected (no admin rights)", err: "No admin rights", userRights: userRights, isNewUser: false };
                this.loginResponses.next(loginRsp);
                return null;
            }

            // Await next user login attempt
            loginRsp = await this.nextLoginResponse();
            if(!loginRsp) {
                console.error("No login response?? ", loginRsp);
                throw 'No login response?';
            }
            // Continue loop with new userRights
            userRights = loginRsp.userRights;
        }

        let errMsg = "User tried to login more than " + maxAttempts + " times!! This seems suspecious";
        console.error(errMsg);
        throw errMsg;
    }

    private checkUserRights(requireRights: UserRights, userRights: UserRights): boolean {
        // Check if user rights are sufficient
        let anonymousOk = (userRights && (requireRights.isAnonymous == userRights.isAnonymous));
        let superRightsOk = (userRights && (!requireRights.isSuperAdmin || userRights.isSuperAdmin));
        let adminRightsOk = (userRights && (!requireRights.isAdmin || userRights.isAdmin));
        let verifiedRightsOk = (userRights && (!requireRights.isVerified || userRights.isVerified));

        // Return user is ok
        return (anonymousOk && superRightsOk && adminRightsOk && verifiedRightsOk);
    }

    /**
    * Sign in with google provider and handle login flow
    * @return Resolve promise on succesfull sign in
    */
    public loginGoogle(): Promise<LoginRsp> {
        if(this.externalLoginFunction) {
            return this.externalLoginFunction(LoginProvider.Google);
        }
        // Create Google provier
        let provider = new GoogleAuthProvider();
        provider.addScope('email');
        provider.addScope('profile');
        return this.signInWithPopup(provider)
    }

    /**
    * Sign in with Apple provider and handle login flow
    * @return Resolve promise on successful sign in
    */
    public async loginApple(): Promise<LoginRsp> {
        if(this.externalLoginFunction) {
            return this.externalLoginFunction(LoginProvider.Apple);
        }

        // Get current user before log in and store its db entry
        const currentUser = await this.auth.currentUser;
        let anonymousUser = currentUser ? await this.userFactory.getUser(currentUser.uid) : null;

        // Sign in with pop-up (create first user with random uid and provider data)
        const credential = await this.appleLoginService.signInWithPopup(currentUser).catch( async (error: FirebaseError) => {
            // If linking fails (user is already linked), sign in without linking
            if(error.code == 'auth/credential-already-in-use') {
                const credential = OAuthProvider.credentialFromError(error);
                return await signInWithCredential(this.auth, credential).catch( secondError => {
                    console.log("Apple credential login error after credential-already-in-use", secondError);
                    return { msg: "Login failed", err: "Could not sign in with apple credentials.", userRights: null, isNewUser: false };
                });
            }
            else {
                console.log("Apple login error", error);
                return { msg: "Login failed", err: "Could not sign in or link with apple popup.", userRights: null, isNewUser: false };
            }
        });
        if('err' in credential) return credential;

        // Ensure that UID is the same on web login and apple login by creating a custom token with StoryHunt Apple UID
        const idToken = await credential.user.getIdToken();
        // Convert Firebase Apple tokenId into a custom Firebase login token (with same UID as on native login)
        const customToken = await this.appleLoginService.getCustomFirebaseToken(idToken);
        if(!customToken) {
            const rsp = { msg: "Login failed", err: "Could not get custom token from API.", userRights: null, isNewUser: false };
            console.warn("Error on login ", rsp.err);
            this.loginResponses.next(rsp);
            return rsp;
        }

        // Sign in with custom token (create second user with uid created from Apple ID and no provider data)
        let customCredential = await signInWithCustomToken(this.auth, customToken);
        // Merge info from original credentials with custom token
        let customCredentialInfo = this.appleLoginService.getLoginCredentialsFirebase(credential, customCredential);
        // Extract relevant anonymous data for migration
        const migrationData: LoginMigrationData = { unlockedMotes: anonymousUser?.unlockedMotes, challengeStateMap: anonymousUser?.challengeStateMap };

        // Start general sign in handling
        return this.handleLogin(customCredentialInfo, null, migrationData);
    }

    /**
    * Sign in with apple user token (authorizationCode) and handle login flow
    * This will validate the authorization code against apples servers through the StoryHunt auth endpoint and
    * login via custom Firebase token.
    * @param userInfo Provider data object like userName, email etc.
    * @param authorizationCode Apple authorization code from OAuth session
    * @return Resolve promise on successful sign in
    */
    public async loginWithAppleToken(userInfo: LoginUser, authorizationCode: string): Promise<LoginCredential> {
        // Get custom token from
        let token = await this.appleLoginService.getCustomAppleToken(authorizationCode);
        if(!token) return null;

        // Sign in as firebase user
        let credential = await signInWithCustomToken(this.auth, token);
        // Set token in user info and overwrite apple uid with custom token id (that fits with the custom token)
        userInfo.token = token;
        userInfo.uid = credential.user.uid;

        // Merge Apple data into login credentials
        return this.appleLoginService.getLoginCredentials(userInfo, credential);
    }

    /**
    * Sign in with facebook provider and handle login flow
    * @return Resolve promise on succesfull sign in
    */
    public loginFacebook(): Promise<LoginRsp> {
        if(this.externalLoginFunction) {
            return this.externalLoginFunction(LoginProvider.Facebook);
        }

        // Create Facebook provider
        let provider = new FacebookAuthProvider();
        provider.addScope('email');
        provider.addScope('public_profile');

        return this.signInWithPopup(provider)
    }

    /**
    * Sign in with MS Azure custom provider and handle login flow
    * @return Resolve promise on succesfull sign in
    */
    public async loginAzure(): Promise<LoginRsp> {
        if(this.externalLoginFunction) {
            console.warn("External login is not supported for Azure, trying to continue with intern..");
            // return this.externalLoginFunction(LoginProvider.Azures);
        }
        // Create Azure provider
        const provider = new OAuthProvider('oidc.oidc.azure');

        // provider.setCustomParameters({ prompt: 'select_account' });
        provider.addScope('openid');
        provider.addScope('email');
        provider.addScope('profile');

        let loginError;

        const oidcUser: UserCredential = await signInWithPopup(this.auth, provider).catch( err => { 
            loginError = err;
            return null; 
        });

        if(!oidcUser || loginError) {
            let errorRsp = { msg: "OIDC Login Failed", err: loginError || "Unknown error", userRights: null, isNewUser: false }
            this.loginResponses.next(errorRsp);
            return errorRsp;
        }

        // Mint custom token with OID as UID
        const idToken = await oidcUser.user.getIdToken();
        const scimUser = await this.oidcLoginService.getCustomOidcToken(idToken).catch( err => {
            loginError = err;
        });
 
        if(!scimUser) {
            // Logout the OIDC user
            signOut(this.auth);
            let errorRsp = { msg: "Could not get oidc token from API", err: loginError || "Unknown error", userRights: null, isNewUser: false };
            this.loginResponses.next(errorRsp);
            return errorRsp;
        }

        // Sign in using custom token
        let firebaseCredentials = signInWithCustomToken(this.auth, scimUser.token);

        // Start general sign in handling
        return this.handleLogin(firebaseCredentials);
    }

    /**
    * Sign in with Firebase user (email and password) and handle login flow
    * @return Resolve promise on succesfull sign in
    */
    public loginWithEmail(email: string, password: string): Promise<LoginRsp> {
        // Sign in with firebase user
        let promise = signInWithEmailAndPassword(this.auth, email, password);
        // Start general sign in handling
        return this.handleLogin(promise);
    }

    /**
    * Sign in with google user token and handle login flow
    * @return Resolve promise on succesfull sign in
    */
    public async loginWithToken(providerId: LoginProvider, token: string, userInfo?: LoginUser, authorizationCode?: string): Promise<LoginRsp> {
        // Get current user before log in and store its db entry
        const currentUser = await this.auth.currentUser;
        let anonymousUser = currentUser ? await this.userFactory.getUser(currentUser.uid) : null;

        // Select login provider
        let credential = this.getLoginCredential(providerId, token, userInfo, authorizationCode);
        if(!credential) {
            const rsp = { msg: "Login failed", err: "Could not get custom token from API.", userRights: null, isNewUser: false };
            console.warn("Error on login ", rsp.err);
            this.loginResponses.next(rsp);
            return rsp;
        }

        // Extract relevant anonymous data for migration
        const migrationData: LoginMigrationData = { unlockedMotes: anonymousUser?.unlockedMotes, challengeStateMap: anonymousUser?.challengeStateMap };

        // Start general sign in handling
        return this.handleLogin(credential, null, migrationData);
    }

    private getLoginCredential(providerId: LoginProvider, idToken: string, userInfo?: LoginUser, authorizationCode?: string): Promise<FirebaseUserCredential> | Promise<LoginCredential> {
        switch(providerId) {
            case LoginProvider.Google:
                let googleCredential = GoogleAuthProvider.credential(idToken);
                return signInWithCredential(this.auth, googleCredential);

            case LoginProvider.Facebook:
                let facebookCredential = FacebookAuthProvider.credential(idToken);
                return signInWithCredential(this.auth, facebookCredential);

            case LoginProvider.Apple:
                return this.loginWithAppleToken(userInfo, authorizationCode);

            default:
                console.warn("Unsupported external login provider ", providerId);
                return null;
        }
    }

    /**
    * Sign in with external DreamHack provider and handle login flow
    * @return Resolve promise on succesfull sign in
    */
    public async loginWithDreamHackToken(idToken: string): Promise<LoginRsp> {
        let errorMsg = "";

        // Get current user before log in and store its db entry
        const currentUser = await this.auth.currentUser;
        let anonymousUser = (currentUser && currentUser.isAnonymous) ? await this.userFactory.getUser(currentUser.uid) : null;

        let loginUser = await this.dreamHackLoginService.getCustomDreamHackToken(idToken).catch((error) => {
            errorMsg = error;
            return null;
        });

        if(!loginUser || !loginUser.token) {
            let errorRsp = { msg: "DreamHack Login Failed", err: errorMsg || "Unknown error", userRights: null, isNewUser: false }
            this.loginResponses.next(errorRsp);
            return errorRsp;
        }
        // Login with custom token
        let firebaseCredentials = (await signInWithCustomToken(this.auth, loginUser.token));
        // Merge DreamHack data into login credentials
        let loginCredentials = this.dreamHackLoginService.getLoginCredentials(loginUser, firebaseCredentials);
        // Extract relevant anonymous data for migration
        const migrationData: LoginMigrationData = { unlockedMotes: anonymousUser?.unlockedMotes, challengeStateMap: anonymousUser?.challengeStateMap };

        // Start general sign in handling
        return this.handleLogin(loginCredentials, null, migrationData);
    }


    /**
    * Sign in with a anonymous Firebase user and handle login flow
    * @return Resolve promise on succesfull sign in
    */
    public loginAnonymous(): Promise<LoginRsp> {
        const promise = signInAnonymously(this.auth);
        return this.handleLogin(promise);
    }

    private async createUser(credentials: LoginCredential, userRights: UserRights, username: string, migrationData?: LoginMigrationData): Promise<boolean> {
        // Get additional user info (is this a new user?) - typecast to UserCredential is ok for now (LoginCredential implements UserCredential partly)
        // but consider removing the LoginCredential type completely?
        let additionalUserInfo = getAdditionalUserInfo(credentials as UserCredential);
        let isNewFbUser = additionalUserInfo?.isNewUser;
        // If there is no user factory nothing more todo, indicate if it is a new fb user
        if(!this.userFactory) {
            if(isNewFbUser) console.error("No user factory, cant create user");
            return Promise.resolve(isNewFbUser);
        }

        const linkingUnderway = (credentials.operationType == 'link');
        // Test if user exists in database
        let userExists = await this.userFactory.userExists(credentials.user.uid);
        // Nothing more todo if user already exists, and should not be linked with a new user
        if(userExists && !linkingUnderway) return false;

        // Create user in database
        if(credentials.user.isAnonymous) await this.userFactory.createAnonymousUser(credentials, userRights);
        else {
            // Manual linking: Add migration data to credentials
            if(migrationData) {
                credentials.user['unlockedMotes'] = migrationData.unlockedMotes;
                credentials.user['challengeStateMap'] = migrationData.challengeStateMap;
            }
            // When linking accounts, we want to remove migration data when updating user,
            // so that we wont override this data in the existing, anonymous user entry.
            // However, when we are linking accounts manually (in case of Apple, DreamHack and native logins),
            // we want to transfer this data to the new user entry, and hence not remove the data before updating (creating) user in db.
            const removeMigrationData = linkingUnderway && !migrationData;
            await this.userFactory.createUser(credentials, userRights, username, removeMigrationData);
        }

        return true;
    }

    /**
     * Sign in with popup and link new users with current, if it is anonymous
     */
    private async signInWithPopup(provider: AuthProvider): Promise<LoginRsp> {
        // Sign in with popup and link with anonymous
        const currentUser = getAuth().currentUser;
        let promise = (currentUser && currentUser.isAnonymous) ? linkWithPopup(currentUser, provider) : signInWithPopup(this.auth, provider);

        // Get credential and create user in db if new
        return this.handleLogin(promise);
    }

    private async handleCredentials(promise: Promise<LoginCredential> | Promise<FirebaseUserCredential>): Promise<[LoginCredential | FirebaseUserCredential, LoginRsp]> {
        let rsp: LoginRsp;

        let credentials = await promise.catch(async (err: FirebaseError) => {
            if(err.code == 'auth/credential-already-in-use') {
                const authCredential = OAuthProvider.credentialFromError(err);
                // Otherwise, create a new promise by signing in without linking
                return await signInWithCredential(this.auth, authCredential).catch( secondError => {
                    rsp = { msg: "Login credential already in use, second credential attempt failed", err: secondError, userRights: null, isNewUser: false };
                    this.loginResponses.next(rsp);
                    // Return null credential
                    return null;
                });
            }
            // Create error response
            rsp = { msg: "Login failed", err: err, userRights: null, isNewUser: false };
            this.loginResponses.next(rsp);
            // Return null credential
            return null;
        });

        return [credentials, rsp];
    }

    private async handleLogin(promise: Promise<FirebaseUserCredential> | Promise<LoginCredential>, username: string = '', migrationData?: LoginMigrationData): Promise<LoginRsp> {
        // Get credentials from promise
        let [credentials, rsp] = await this.handleCredentials(promise);

        // Return login response on error
        if(!credentials) return { msg: "Login failed", err: rsp?.err || "Unknown error", userRights: null, isNewUser: false };

        // Decode user token
        const userRights = await this.getUserRights(credentials.user);

        // Create a new db entry for new users
        const isNewUser = await this.createUser(credentials, userRights, username, migrationData);

        // Update user token
        await this.refreshUserToken();

        // Notify listeners about success
        rsp = { msg: "Login ok", err: "", userRights: userRights, isNewUser: isNewUser };
        this.loginResponses.next(rsp);
        return rsp;
    }

    public logout(): Promise<boolean> {
        this.adminUser = null;

        return new Promise((resolve, reject) => {
            this.userRights = new UserRights();
            signOut(this.auth).then((sucess) => {
                let rsp = { msg: "Logout success", err: '', userRights: null, isNewUser: false };
                this.loginResponses.next(rsp);
                resolve(true);
            }, (error) => {
                let rsp = { msg: "Logout failed", err: '', userRights: null, isNewUser: false };
                this.loginResponses.next(rsp);
                resolve(false);
            });
        });
    }

    public registerLoginAbort(): void {

        // let event = this.loginSubject.getValue();
        // event.type = LoginEventType.abort;
        // event.error = new Error('Popup closed before user finalized login');
        // event.error.name = 'popup-closed';
        // event.error.code = 'login-service/internal-popup-closed-by-user';
        //this.loginSubject.next(event);
        // TODO: What todo?
    }

    public async createUserWithEmail(email: string, pw: string, username: string): Promise<LoginRsp> {
        const currentUser = await this.auth.currentUser;
        let promise: Promise<FirebaseUserCredential>;

        // Link new user with anonymous
        if(currentUser && currentUser.isAnonymous) {
            const credential = EmailAuthProvider.credential(email, pw);
            promise = linkWithCredential(currentUser, credential).catch((err) => {
                // If linking fails, create fb user without linking (should not happen as this function is for new users only)
                return createUserWithEmailAndPassword(this.auth, email, pw);
            })
        }
        // Create user without linking
        else promise = createUserWithEmailAndPassword(this.auth, email, pw);

        // Get credential and create user in db
        return this.handleLogin(promise, username);
    }

    public watchLoginResponses(): Observable<LoginRsp> {
        return this.loginResponses;
    }

    /**
    * Recover email (using angularfire sendPasswordResetEmail)
    */
    public recoverPassword(email: string): Promise<void> {
        return sendPasswordResetEmail(this.auth, email);
    }

    /**
    * Update motes email user password
    * @param password
    */
    public updatePassword(password: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.getCurrentUser().then(user => {
                updatePassword(user, password)
                    .then(() => resolve(true))
                    .catch(err => reject(err));
            });
        });
    }

    public watchIdToken(): Observable<string> {
        // Watch firebase user token refresh
        return idToken(this.auth);
    }

    public refreshUserToken(): Promise<UserRights> {
        return new Promise(async (resolve, reject) => {
            let fbUser = await this.getCurrentUser();
            if(!fbUser) return reject(new UserRights());

            let currentUserRights = await this.getUserCurrentRights();

            fbUser.getIdToken(true).then((token) => {
                // Get new user rights
                this.getUserRights(fbUser).then(userRights => {
                    if(!this.doesUserRightsEqual(currentUserRights, userRights)) {
                        this.firebaseUserSubject.next(fbUser);
                    }
                    resolve(userRights);
                })
            });
        });
    }

    public async getAuthOptions(): Promise<{ headers: HttpHeaders }> {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': [await this.getIdToken()]
        });
        return { headers: headers };
    }

    public getIdToken(): Promise<string> {
        return this.watchIdToken().pipe(first()).toPromise();
    }

    private doesUserRightsEqual(a: UserRights, b: UserRights): boolean {
        let uid = (a.uid == b.uid);
        let isAdmin = (a.isAdmin == b.isAdmin);
        let isAnonymous = (a.isAnonymous == b.isAnonymous);
        let isSuperAdmin = (a.isSuperAdmin == b.isSuperAdmin);
        let isVerified = (a.isVerified == b.isVerified);

        return (uid && isAdmin && isAnonymous && isSuperAdmin && isVerified);
    }

}
